import React, { useState } from 'react';
import { Cloud, CloudOff, RefreshCw, Check } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { isFirebaseConfigured } from '../config/firebaseConfig';

const STATUS_META = {
  local: { icon: CloudOff, text: 'לא מסונכרן - שמור רק במכשיר הזה', color: 'text-stone-500' },
  syncing: { icon: RefreshCw, text: 'מסנכרן…', color: 'text-brand-500' },
  synced: { icon: Cloud, text: 'מסונכרן', color: 'text-brand-600' },
  error: { icon: CloudOff, text: 'הסנכרון נכשל - הנתונים עדיין שמורים במכשיר', color: 'text-bloom-600' },
};

const SyncSettings = () => {
  const { syncStatus, enableCrossDeviceSync, signInForSync } = useApp();
  const [mode, setMode] = useState(null); // null | 'create' | 'signin'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (!isFirebaseConfigured) {
    return (
      <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm">
        <h3 className="font-bold text-stone-800 flex items-center gap-2 mb-1">
          <CloudOff className="w-4 h-4 text-stone-400" /> סנכרון בין מכשירים
        </h3>
        <p className="text-xs text-stone-500">
          לא הוגדר עדיין פרויקט Firebase לאפליקציה הזו (משתני הסביבה REACT_APP_FIREBASE_*
          חסרים). הנתונים נשמרים רק במכשיר הזה. ראה DEPLOY.md להוראות הגדרה.
        </p>
      </div>
    );
  }

  const { icon: StatusIcon, text: statusText, color: statusColor } = STATUS_META[syncStatus] || STATUS_META.local;

  const submit = async () => {
    if (!email.trim() || password.length < 6) {
      setError('אימייל תקין וסיסמה של 6 תווים לפחות');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (mode === 'create') {
        await enableCrossDeviceSync(email.trim(), password);
      } else {
        await signInForSync(email.trim(), password);
      }
      setDone(true);
    } catch (e) {
      setError(
        e?.code === 'auth/email-already-in-use' ? 'האימייל הזה כבר משויך לחשבון - נסה "יש לי כבר קוד"' :
        e?.code === 'auth/wrong-password' || e?.code === 'auth/invalid-credential' ? 'סיסמה שגויה' :
        e?.code === 'auth/user-not-found' ? 'לא נמצא חשבון עם האימייל הזה' :
        'משהו נכשל. נסה שוב.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-stone-800 flex items-center gap-2">
          <Cloud className="w-4 h-4 text-brand-500" /> סנכרון בין מכשירים
        </h3>
        <span className={`flex items-center gap-1 text-xs font-semibold ${statusColor}`}>
          <StatusIcon className={`w-3 h-3 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
          {statusText}
        </span>
      </div>

      {done ? (
        <div className="flex items-center gap-2 text-brand-600 text-sm mt-2">
          <Check className="w-4 h-4" /> מחובר! פתח את האפליקציה עם אותו אימייל במכשיר השני כדי לראות את הנתונים שם.
        </div>
      ) : !mode ? (
        <div className="space-y-2 mt-2">
          <p className="text-xs text-stone-500 mb-2">
            הנתונים כבר נשמרים אוטומטית בענן, אבל כדי לראות אותם במכשיר נוסף צריך לקשר אימייל.
          </p>
          <button
            onClick={() => setMode('create')}
            className="w-full bg-brand-500 text-white py-2 rounded-lg font-semibold text-sm hover:bg-brand-600 transition"
          >
            קשר אימייל למכשיר הזה
          </button>
          <button
            onClick={() => setMode('signin')}
            className="w-full bg-stone-100 text-stone-700 py-2 rounded-lg font-semibold text-sm hover:bg-stone-200 transition"
          >
            כבר קישרתי אימייל - התחבר במכשיר הזה
          </button>
        </div>
      ) : (
        <div className="space-y-2 mt-2">
          <input
            type="email"
            placeholder="אימייל"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
          <input
            type="password"
            placeholder="סיסמה (לפחות 6 תווים)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
          {error && <p className="text-xs text-bloom-600">{error}</p>}
          <div className="flex gap-2">
            <button
              onClick={submit}
              disabled={busy}
              className="flex-1 bg-brand-500 text-white py-2 rounded-lg font-semibold text-sm hover:bg-brand-600 transition disabled:opacity-50"
            >
              {busy ? 'רגע…' : mode === 'create' ? 'קשר אימייל' : 'התחבר'}
            </button>
            <button
              onClick={() => { setMode(null); setError(''); }}
              className="flex-1 bg-stone-100 text-stone-700 py-2 rounded-lg font-semibold text-sm hover:bg-stone-200 transition"
            >
              ביטול
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncSettings;
